import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import type { FormEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetList, useNotify, useTranslate } from "react-admin";

import {
  CreateDeviceEnrollmentTokenDocument,
  CreateDeviceEnrollmentTokenMutation,
  CreateDeviceEnrollmentTokenMutationVariables,
  DeviceEnrollmentScopeType,
} from "../../generated/graphql";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { client } from "../../lib/apolloClient";
import {
  buildCreateTokenInput,
  CreateTokenFormError,
  CreateTokenFormState,
  DEFAULT_DURATION_HOURS,
  DURATION_OPTIONS,
  getScopeLocationType,
  getScopeTypeLabel,
  SCOPE_TYPE_HINTS,
  SCOPE_TYPE_ORDER,
  scopeTypeRequiresLocation,
  validateCreateTokenForm,
} from "../../lib/deviceEnrollment";
import { borderRadius, color, typography } from "../../theme/designTokens";
import { dialogContentSx, dialogPaperSx, dialogTitleSx } from "../../theme/dialogStyles";
import type { DeviceEnrollmentTokenRecord } from "../../types/deviceEnrollment";
import { buildPhysicalLocationTree, LocationRecord } from "../LocationHierarchyFields";
import type { EnrollmentTokenSecret } from "./DeviceEnrollmentTokenDialog";

export type EnrollmentScopePreset = {
  scopeType: DeviceEnrollmentScopeType;
  locationId?: string | null;
};

export type CreatedEnrollmentToken = {
  token: DeviceEnrollmentTokenRecord;
  secret: EnrollmentTokenSecret;
};

type Props = {
  open: boolean;
  organizationId: string;
  preset?: EnrollmentScopePreset;
  onClose: () => void;
  onCreated: (created: CreatedEnrollmentToken) => void;
};

type ScopeLocationChoice = {
  id: string;
  label: string;
  depth: number;
};

const LOCATION_PAGE_SIZE = 1000;
const INDENT_PER_DEPTH_PX = 16;

/* Locations of the type the scope needs, including their physical parent path. */
export const listScopeLocationChoices = (
  locations: LocationRecord[],
  scopeType: DeviceEnrollmentScopeType
): ScopeLocationChoice[] => {
  const locationType = getScopeLocationType(scopeType);
  if (!locationType) return [];

  const tree = buildPhysicalLocationTree(locations);
  const flatten = (nodes: LocationRecord[], parentNames: string[], depth: number): ScopeLocationChoice[] =>
    nodes.flatMap(node => {
      const locationNames = [...parentNames, node.name ?? ""];
      return [
        ...(node.type === locationType ? [{ id: node.id, label: locationNames.join(" > "), depth }] : []),
        ...flatten(tree.childrenMap.get(node.id) || [], locationNames, depth + 1),
      ];
    });

  return flatten(tree.roots, [], 0);
};

const initialForm = (organizationId: string, preset?: EnrollmentScopePreset): CreateTokenFormState => ({
  organizationId,
  scopeType: preset?.scopeType ?? DeviceEnrollmentScopeType.Organization,
  locationId: preset?.locationId ?? null,
  durationHours: DEFAULT_DURATION_HOURS,
  maxUses: 1,
});

export const CreateDeviceEnrollmentTokenDialog = ({ open, organizationId, preset, onClose, onCreated }: Props) => {
  const translate = useTranslate();
  const notify = useNotify();
  const handleError = useErrorHandler();

  const [form, setForm] = useState<CreateTokenFormState>(() => initialForm(organizationId, preset));
  const [maxUsesText, setMaxUsesText] = useState("1");
  const [submitted, setSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initialForm(organizationId, preset));
      setMaxUsesText("1");
      setSubmitted(false);
    }
  }, [open, organizationId, preset]);

  const { data: locations = [], isLoading: locationsLoading } = useGetList<LocationRecord>(
    "Location",
    {
      filter: { organizationId },
      pagination: { page: 1, perPage: LOCATION_PAGE_SIZE },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: open && !!organizationId }
  );

  const locationChoices = useMemo(
    () => listScopeLocationChoices(locations, form.scopeType),
    [locations, form.scopeType]
  );

  const validation = validateCreateTokenForm(form);
  const showErrors = submitted && !validation.valid;

  const selectScope = (scopeType: DeviceEnrollmentScopeType): void => {
    setForm(current => ({
      ...current,
      scopeType,
      locationId: scopeType === current.scopeType ? current.locationId : null,
    }));
  };

  const handleMaxUsesChange = (value: string): void => {
    setMaxUsesText(value);
    const parsed = value.trim() === "" ? null : Number(value);
    setForm(current => ({ ...current, maxUses: parsed }));
  };

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setSubmitted(true);
      if (!validation.valid || isSaving) return;

      setIsSaving(true);
      try {
        const result = await client.mutate<
          CreateDeviceEnrollmentTokenMutation,
          CreateDeviceEnrollmentTokenMutationVariables
        >({
          mutation: CreateDeviceEnrollmentTokenDocument,
          fetchPolicy: "no-cache",
          variables: { input: buildCreateTokenInput(form) },
        });
        const payload = result.data?.createDeviceEnrollmentToken;
        if (!payload) throw new Error("createDeviceEnrollmentToken returned no data");

        notify("deviceEnrollment.create.success", { type: "success" });
        onCreated({ token: payload.token, secret: { code: payload.code, qrPayload: payload.qrPayload } });
        onClose();
      } catch (error) {
        handleError(error, translate("deviceEnrollment.create.error"), { operation: "createDeviceEnrollmentToken" });
      } finally {
        setIsSaving(false);
      }
    },
    [form, handleError, isSaving, notify, onClose, onCreated, translate, validation.valid]
  );

  const locationRequired = showErrors && validation.errors.includes(CreateTokenFormError.LocationRequired);
  const maxUsesInvalid = showErrors && validation.errors.includes(CreateTokenFormError.MaxUsesNotPositiveInteger);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: dialogPaperSx }}
      data-testid="create-device-enrollment-token-dialog"
    >
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle sx={{ ...dialogTitleSx, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold }}>
            {translate("deviceEnrollment.create.title")}
          </Typography>
          <IconButton aria-label={translate("ra.action.close")} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ ...dialogContentSx, display: "grid", gap: 3 }}>
          <Typography variant="body2">{translate("deviceEnrollment.create.description")}</Typography>

          <Box>
            <Typography variant="body2" fontWeight={typography.fontWeight.medium} sx={{ mb: 1 }}>
              {translate("deviceEnrollment.create.scopeLabel")}
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
              {SCOPE_TYPE_ORDER.map(scopeType => {
                const selected = form.scopeType === scopeType;
                return (
                  <Box
                    key={scopeType}
                    component="button"
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => selectScope(scopeType)}
                    data-testid={`enrollment-scope-${scopeType.toLowerCase()}`}
                    sx={{
                      textAlign: "left",
                      cursor: "pointer",
                      p: 1.5,
                      border: `1px solid ${selected ? color.selectedOptionBorder : color.panelBorder}`,
                      borderRadius: borderRadius.md,
                      backgroundColor: selected ? color.selectedOptionBackground : color.white,
                      font: "inherit",
                      color: color.typographyBase,
                    }}
                  >
                    <Typography fontWeight={typography.fontWeight.medium}>
                      {translate(getScopeTypeLabel(scopeType))}
                    </Typography>
                    <Typography variant="body2" color={color.gray}>
                      {translate(SCOPE_TYPE_HINTS[scopeType])}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {scopeTypeRequiresLocation(form.scopeType) && (
            <FormControl fullWidth required error={locationRequired}>
              <InputLabel id="enrollment-scope-location-label">
                {translate(getScopeTypeLabel(form.scopeType))}
              </InputLabel>
              <Select
                labelId="enrollment-scope-location-label"
                label={translate(getScopeTypeLabel(form.scopeType))}
                value={form.locationId ?? ""}
                onChange={event => setForm(current => ({ ...current, locationId: String(event.target.value) }))}
                disabled={locationsLoading}
                inputProps={{ "data-testid": "enrollment-scope-location" }}
              >
                {locationChoices.map(choice => (
                  <MenuItem key={choice.id} value={choice.id} sx={{ pl: 2 + (choice.depth * INDENT_PER_DEPTH_PX) / 8 }}>
                    {choice.label}
                  </MenuItem>
                ))}
              </Select>
              {locationRequired && (
                <FormHelperText>{translate("deviceEnrollment.create.locationRequired")}</FormHelperText>
              )}
            </FormControl>
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="enrollment-duration-label">{translate("deviceEnrollment.duration.label")}</InputLabel>
              <Select
                labelId="enrollment-duration-label"
                label={translate("deviceEnrollment.duration.label")}
                value={form.durationHours}
                onChange={event => setForm(current => ({ ...current, durationHours: Number(event.target.value) }))}
                inputProps={{ "data-testid": "enrollment-duration" }}
              >
                {DURATION_OPTIONS.map(option => (
                  <MenuItem key={option.hours} value={option.hours}>
                    {translate(option.labelKey)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              type="number"
              label={translate("deviceEnrollment.usage.maxUsesLabel")}
              value={maxUsesText}
              onChange={event => handleMaxUsesChange(event.target.value)}
              error={maxUsesInvalid}
              helperText={
                maxUsesInvalid
                  ? translate("deviceEnrollment.create.maxUsesInvalid")
                  : translate("deviceEnrollment.usage.unlimited")
              }
              inputProps={{ min: 1, step: 1, "data-testid": "enrollment-max-uses" }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button color="secondary" onClick={onClose} data-testid="create-device-enrollment-token-cancel">
            {translate("ra.action.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : undefined}
            sx={{ backgroundColor: color.primary, color: color.white }}
            data-testid="create-device-enrollment-token-submit"
          >
            {translate("deviceEnrollment.action.create")}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
